// The Cases tab: "which individual cases are driving what I see, and which should I look at?"
// The scope on top decides which cases the whole page describes; the duration histogram says what
// normal looks like and doubles as a scope filter; the search and quick subsets on the table find
// the interesting cases without changing any reading; and each row's key notes say why that case
// was different, so a page of cases can be scanned without opening one.
import * as Collapsible from "@radix-ui/react-collapsible";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ChevronRight, MoreHorizontal, Route, SlidersHorizontal } from "lucide-react";
import React from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Process } from "../../api/types";
import { formatDuration, parseUtcDate } from "../../utils";
import { AppSelect } from "../AppSelect";
import ProcessCaseCharts from "./ProcessCaseCharts";
import {
  CaseNoteBadges,
  type CaseNoteContext,
  CaseNotesLegend,
  caseNoteContext,
  useCaseNoteDescription,
} from "./ProcessCaseNoteBadges";
import { useProcessSourceIdentity } from "./ProcessInstancePicker";
import type { CasesIntent } from "./ProcessPanelTabs";
import type { CaseReading, CaseSubset } from "./processCaseNotes";
import type { ProcessInstance, StepNamesById } from "./processGraphModel";
import {
  FilterChip,
  KpiTiles,
  PanelSearchInput,
  SortHeader,
  TABLE_PAGE_SIZE,
  TablePager,
} from "./processSidebarUi";
import {
  type CaseListFilters,
  EMPTY_CASE_LIST_FILTERS,
  type ProcessCaseSelection,
} from "./useProcessCaseSelection";
import type { ProcessGraphState } from "./useProcessGraph";
import { runTitle } from "./processInstanceLabels";

const SUBSETS: CaseSubset[] = ["all", "longest", "waits", "variations", "rework"];

type SortColumn = "case" | "duration";

const TABLE_GRID =
  "grid grid-cols-[minmax(0,1fr)_auto_1rem] gap-x-4 @xl:grid-cols-[minmax(0,2fr)_5.25rem_minmax(0,1fr)_1rem]";

const startedAt = (instance: ProcessInstance) => Date.parse(instance.source.traceStart) || 0;

/** Marks a case the graph draws while it holds hand-picked cases. */
const InGraphDot = () => (
  <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-info" />
);

const CaseRow = ({
  reading,
  label,
  nameById,
  context,
  outlierAbove,
  onOpen,
  checked,
  onToggle,
  inGraph,
}: {
  reading: CaseReading;
  /** The case's own name, for cases whose analysis wrote no description. */
  label: string;
  nameById: StepNamesById;
  context: CaseNoteContext;
  outlierAbove: number | null;
  onOpen: (instanceId: string) => void;
  checked: boolean;
  onToggle?: () => void;
  inGraph: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const describe = useCaseNoteDescription(nameById, context);
  const descriptionId = useId();
  const { instance } = reading;
  const description = runTitle(instance, label);
  const start = parseUtcDate(instance.source.traceStart);
  const date = start.toLocaleString(i18n?.resolvedLanguage, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <li className={`flex items-start ${checked ? "bg-info/[0.035]" : ""}`}>
      {onToggle && (
        <label className="flex shrink-0 cursor-pointer items-center p-3 pt-3.5">
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            aria-label={t("processPanel.selectCase", { label: description })}
            className="h-4 w-4 accent-info"
          />
        </label>
      )}
      <div
        className={`group relative ${TABLE_GRID} min-w-0 flex-1 items-center gap-y-2 py-2.5 ${onToggle ? "pl-0" : "pl-3"} pr-3 text-left transition hover:bg-surface-strong`}
      >
        {/* Stretch the open target across the row, leaving note buttons as separate controls. */}
        <Tooltip.Provider delayDuration={350}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                onClick={() => onOpen(instance.id)}
                aria-label={t("processPanel.openCase", { label: description })}
                aria-describedby={descriptionId}
                className="min-w-0 text-left after:absolute after:inset-0 focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-primary/30"
              >
                <span id={descriptionId} className="sr-only">
                  {inGraph && `${t("processPanel.shownInGraph")}. `}
                  {describe(reading.notes)}
                </span>
                <span className="flex min-w-0 items-center gap-1.5">
                  {inGraph && <InGraphDot />}
                  <span className="truncate text-sm font-medium text-text">{description}</span>
                </span>
                <span className="mt-0.5 flex min-w-0 items-baseline gap-3 text-xs">
                  {instance.source.userName && (
                    <span className="min-w-0 truncate text-text-mid">
                      {instance.source.userName}
                    </span>
                  )}
                  <span className="shrink-0 text-text-dim">{date}</span>
                </span>
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="top"
                align="start"
                sideOffset={8}
                collisionPadding={16}
                className="pd-theme z-[60] w-[28rem] max-w-[calc(100vw-32px)] max-h-[var(--radix-tooltip-content-available-height)] overflow-y-auto break-words rounded-lg border border-outline bg-surface px-4 py-3 text-sm leading-relaxed text-text shadow-lift"
              >
                {description}
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
        <span
          title={
            reading.outlier && outlierAbove !== null
              ? t("processPanel.outlierDuration", {
                  threshold: formatDuration(outlierAbove),
                })
              : undefined
          }
          className={`text-right text-sm tabular-nums ${
            reading.outlier ? "font-semibold text-danger-strong" : "text-text-mid"
          }`}
        >
          {formatDuration(reading.duration)}
        </span>
        <CaseNoteBadges
          notes={reading.notes}
          nameById={nameById}
          context={context}
          className="relative z-10 col-span-3 row-start-2 @xl:col-span-1 @xl:col-start-3 @xl:row-start-1"
        />
        <ChevronRight
          aria-hidden="true"
          className="pointer-events-none col-start-3 row-start-1 h-4 w-4 text-text-dim transition group-hover:text-text @xl:col-start-4"
        />
      </div>
    </li>
  );
};

const CaseGroup = ({
  name,
  readings,
  renderCases,
}: {
  name: string;
  readings: CaseReading[];
  renderCases: (readings: CaseReading[]) => React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(readings.length / TABLE_PAGE_SIZE) - 1));
  return (
    <Collapsible.Root>
      <h3>
        <Collapsible.Trigger className="group flex w-full items-center gap-2 border-b border-outline bg-surface-strong/50 px-3 py-2.5 text-left text-sm font-medium text-text-mid transition hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30">
          <ChevronRight
            aria-hidden="true"
            className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-90"
          />
          <span className="min-w-0 flex-1 break-words">{name}</span>
          <span className="shrink-0 text-xs tabular-nums text-text-dim">
            {t("n_cases", { count: readings.length })}
          </span>
        </Collapsible.Trigger>
      </h3>
      <Collapsible.Content>
        {renderCases(
          readings.slice(currentPage * TABLE_PAGE_SIZE, (currentPage + 1) * TABLE_PAGE_SIZE),
        )}
        <TablePager page={currentPage} total={readings.length} onPageChange={setPage} />
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

const ProcessCasesTab = ({
  process,
  nameById,
  intent,
  onOpenCase,
  onAskVariability,
  state,
  selection,
}: {
  process: Process;
  state: ProcessGraphState;
  selection: ProcessCaseSelection;
  nameById: StepNamesById;
  /** Where another tab asked this one to open: a subset, or the cases of one path. */
  intent?: CasesIntent;
  onOpenCase: (instanceId: string) => void;
  /** Offered on the histogram when case durations spread unusually wide. */
  onAskVariability?: () => void;
}) => {
  const { t } = useTranslation();
  const { base, filters, list, graphIds, matching } = selection;
  const { cases, profile, subsets } = base;
  const context = useMemo(() => caseNoteContext(base), [base]);
  const identityFor = useProcessSourceIdentity(process);
  const [sort, setSort] = useState<{ column: SortColumn; descending: boolean }>({
    column: "case",
    descending: true,
  });
  const [page, setPage] = useState(0);
  const filterKey = JSON.stringify(filters);
  // Page-wide filters can change from the shared bar or another tab's chart.
  useEffect(() => setPage(0), [filterKey]);
  const [group, setGroup] = useState("none");
  // The parent keys this tab by navigation intent; apply each intent only on entry, as the whole
  // of what the list shows rather than on top of a search left from an earlier visit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: A navigation intent is consumed once per mount.
  useEffect(() => {
    if (intent)
      selection.changeList(
        "subset" in intent
          ? { ...EMPTY_CASE_LIST_FILTERS, subset: intent.subset }
          : {
              ...EMPTY_CASE_LIST_FILTERS,
              path: { ids: intent.ids, label: intent.label },
            },
      );
  }, []);
  const rows = useMemo(() => {
    const direction = sort.descending ? -1 : 1;
    return base.cases
      .filter(reading => selection.matching.has(reading.instance.id))
      .sort((a, b) =>
        sort.column === "duration"
          ? direction * (a.duration - b.duration)
          : direction * (startedAt(a.instance) - startedAt(b.instance)),
      );
  }, [base, selection.matching, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / TABLE_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = rows.slice(currentPage * TABLE_PAGE_SIZE, (currentPage + 1) * TABLE_PAGE_SIZE);
  const durations = cases.map(reading => reading.duration);
  const averageDuration = durations.length
    ? durations.reduce((sum, value) => sum + value, 0) / durations.length
    : 0;
  const changeFilters = (next: typeof filters) => {
    selection.changeFilters(next);
    setPage(0);
  };
  const changeList = (next: CaseListFilters) => {
    selection.changeList(next);
    setPage(0);
  };
  // Offered once the list is narrowed to something the graph is not already drawing.
  const listNarrowed = list.subset !== "all" || !!list.search.trim() || !!list.path;
  const listInGraph =
    !!graphIds && graphIds.size === matching.size && [...matching].every(id => graphIds.has(id));
  const offerShowInGraph = listNarrowed && !listInGraph && rows.length > 0 && !selection.editing;
  const groups = new Map<string, typeof visible>();
  // Group headers describe the full filtered list, even when their cases need several pages.
  for (const reading of group === "none" ? visible : rows) {
    const key =
      group === "person"
        ? reading.instance.source.userId
        : group === "session"
          ? reading.instance.source.recordingId
          : "";
    groups.set(key, [...(groups.get(key) ?? []), reading]);
  }
  const renderCases = (readings: CaseReading[]) => (
    <ul className="divide-y divide-outline">
      {readings.map(reading => (
        <CaseRow
          key={reading.instance.id}
          reading={reading}
          label={identityFor(reading.instance.source).instanceLabel}
          nameById={nameById}
          context={context}
          outlierAbove={base.profile.outlierAbove}
          onOpen={onOpenCase}
          checked={selection.editing && selection.picks.has(reading.instance.id)}
          onToggle={selection.editing ? () => selection.toggle(reading.instance.id) : undefined}
          inGraph={!!graphIds && !selection.editing && graphIds.has(reading.instance.id)}
        />
      ))}
    </ul>
  );
  return (
    <div
      className="@container"
      data-tour={state.appliedScope.query ? "nl-filter-active" : undefined}
    >
      <KpiTiles
        items={[
          { label: t("processPanel.metricTotalCases"), value: cases.length },
          {
            label: t("processPanel.metricAvgDuration"),
            value: formatDuration(averageDuration),
          },
          {
            label: t("processPanel.metricMedianDuration"),
            value: formatDuration(profile.median),
          },
          {
            label: t("processPanel.metricStepsPerCase"),
            value: (cases.length
              ? cases.reduce((sum, reading) => sum + reading.instance.steps.length, 0) /
                cases.length
              : 0
            ).toFixed(1),
          },
        ]}
      />
      <ProcessCaseCharts
        onReviewOutliers={() => selection.changeList(EMPTY_CASE_LIST_FILTERS)}
        analysis={base}
        reference={selection.durationReference}
        referenceInstances={selection.durationReferenceInstances}
        onAskVariability={onAskVariability}
        durationRange={
          filters.minDuration !== undefined || filters.maxDuration !== undefined
            ? {
                min: filters.minDuration ?? 0,
                max: filters.maxDuration ?? Number.POSITIVE_INFINITY,
              }
            : null
        }
        onDurationRange={range =>
          changeFilters({
            ...filters,
            minDuration: range?.min,
            maxDuration: range && Number.isFinite(range.max) ? range.max : undefined,
          })
        }
      />
      <section
        aria-label={t("processPanel.caseList")}
        className="mt-4 border-t border-outline pt-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg font-semibold leading-snug tracking-tight text-text">
            {t("processPanel.caseList")}
          </h3>
          <button
            type="button"
            aria-label={t("processPanel.editFilters")}
            title={t("processPanel.scopeHelp")}
            onClick={() => state.setFiltersOpen(true)}
            className="inline-flex h-7 items-center gap-1.5 rounded-full border border-outline bg-surface px-2.5 text-xs font-medium text-text-mid transition hover:border-info/30 hover:bg-info/5 hover:text-info focus-visible:ring-2 focus-visible:ring-info/25"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
            {t("filters")}
          </button>
          {graphIds && !selection.editing && (
            <span className="inline-flex items-center gap-1.5 text-xs text-text-dim">
              <InGraphDot />
              {t("processPanel.casesInGraph", { count: graphIds.size })}
            </span>
          )}
          {selection.editing && (
            <div className="flex gap-3 text-xs">
              <button
                type="button"
                onClick={() =>
                  selection.select([
                    ...new Set([...selection.picks, ...rows.map(row => row.instance.id)]),
                  ])
                }
                className="text-info hover:underline"
              >
                {t("processPanel.selectAllMatching", { count: rows.length })}
              </button>
              <button
                type="button"
                onClick={() => selection.select([])}
                className="text-text-mid hover:underline"
              >
                {t("processPanel.clearSelection")}
              </button>
            </div>
          )}
          <div className="ml-auto w-36">
            <AppSelect
              label={t("processPage.groupBy")}
              value={group}
              onChange={value => {
                setGroup(value);
                setPage(0);
              }}
              choices={[
                { id: "none", display: t("processPage.noGrouping") },
                { id: "person", display: t("processPage.byPerson") },
                { id: "session", display: t("processPage.bySession") },
              ]}
            />
          </div>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              aria-label={t("processPanel.caseActions")}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-text-mid hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-info/30"
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={4}
                className="pd-theme dropdown-menu min-w-44 p-1"
              >
                <DropdownMenu.Item
                  disabled={!rows.length}
                  onSelect={selection.begin}
                  className="dropdown-option text-sm"
                >
                  {t("processPanel.chooseCases")}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <PanelSearchInput
            value={list.search}
            onChange={search => changeList({ ...list, search })}
            label={t("processPanel.searchCases")}
          />
          {offerShowInGraph && (
            <button
              type="button"
              onClick={selection.showListInGraph}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-outline bg-surface px-3 text-sm font-medium text-text transition hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              <Route className="h-4 w-4 text-text-dim" aria-hidden="true" />
              {t("processPanel.showCasesInGraph", { count: rows.length })}
            </button>
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {SUBSETS.map(id => {
            const count = subsets[id].size;
            const active = list.subset === id;
            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                title={t(`processPanel.subsetHelp_${id}`)}
                disabled={count === 0 && id !== "all"}
                onClick={() => {
                  changeList({ ...list, subset: id });
                  if (id === "longest") setSort({ column: "duration", descending: true });
                }}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition disabled:cursor-default disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${active ? "border-text bg-text text-surface" : "border-outline bg-surface text-text-mid hover:border-outline-strong hover:text-text"}`}
              >
                {id === "all" ? t("processPanel.allCasesFilter") : t(`processPanel.subset_${id}`)}
                <span className={`tabular-nums ${active ? "text-surface/70" : "text-text-dim"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        {list.path && (
          <div className="mt-2">
            <FilterChip
              label={t("processPanel.pathFilter", { label: list.path.label })}
              onClear={() => changeList({ ...list, path: null })}
            />
          </div>
        )}
        <div className="mt-3 overflow-hidden rounded-lg border border-outline">
          <div
            className={`${TABLE_GRID} border-b border-outline bg-surface-strong/60 ${selection.editing ? "pl-10" : "pl-3"} pr-3 py-2 text-[11px] font-medium uppercase tracking-wide text-text-dim`}
          >
            <SortHeader
              label={t("processPanel.columnCase")}
              column="case"
              sort={sort}
              onSort={column =>
                setSort(current => ({
                  column,
                  descending: current.column === column ? !current.descending : true,
                }))
              }
            />
            <SortHeader
              label={t("processPanel.columnDuration")}
              column="duration"
              align="right"
              sort={sort}
              onSort={column =>
                setSort(current => ({
                  column,
                  descending: current.column === column ? !current.descending : true,
                }))
              }
            />
            <span className="hidden items-center gap-1 @xl:flex">
              {t("processPanel.columnNotes")}
            </span>
          </div>
          {visible.length > 0 ? (
            <div>
              {[...groups].map(([key, readings]) =>
                group !== "none" ? (
                  <CaseGroup
                    key={`${group}:${key}`}
                    name={
                      group === "person"
                        ? readings[0].instance.source.userName || t("user")
                        : identityFor(readings[0].instance.source).recordingName
                    }
                    readings={readings}
                    renderCases={renderCases}
                  />
                ) : (
                  <React.Fragment key={key}>{renderCases(readings)}</React.Fragment>
                ),
              )}
            </div>
          ) : (
            <p className="px-3 py-6 text-center text-sm text-text-dim">
              {t("processPanel.noMatchingCases")}
            </p>
          )}
        </div>
        {group === "none" && (
          <TablePager page={currentPage} total={rows.length} onPageChange={setPage} />
        )}
        <CaseNotesLegend />
      </section>
    </div>
  );
};

export default ProcessCasesTab;
